/**
 * LabLifecycleModal — confirm a lab lifecycle flip (suspend ⇄ reactivate). A suspend genuinely blocks
 * every user of the lab from logging in (the server stamps the lifecycle substrate), so it requires an
 * explicit confirmation with an optional audit motif (journalized). Reactivate is the inverse. Delegates to
 * POST /management/labs/:id/statut (management.lab.onboard), correct-by-reissue + version-guarded: the
 * row's current `version` rides as `expected_version` so a concurrent change surfaces as a typed 409.
 *
 * The dialog is non-dismissable while the write is in flight. On success the management root is invalidated
 * (the row badge + the Overview KPIs re-read) and a toast confirms.
 */
import { useState } from "react";
import { Button, FormField, Modal, Textarea, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useSetLabStatut } from "../screens/onboarding.api";
import type { LabDirectoryRow } from "../types";

/** The action chosen on a row — drives the target statut + the i18n key family. */
export type LabLifecycleAction = "suspend" | "reactivate";

export interface LabLifecycleModalProps {
  lab: LabDirectoryRow | null;
  action: LabLifecycleAction | null;
  onClose: () => void;
}

/** suspend → the lab goes `suspendu`; reactivate → `actif`. */
const TARGET_STATUT = { suspend: "suspendu", reactivate: "actif" } as const;

export function LabLifecycleModal({ lab, action, onClose }: LabLifecycleModalProps) {
  const { t } = useT();
  const toast = useToast();
  const setStatut = useSetLabStatut();
  const [motif, setMotif] = useState("");

  const open = !!lab && !!action;

  const close = () => {
    if (setStatut.isPending) return;
    setMotif("");
    onClose();
  };

  const confirm = () => {
    if (!lab || !action) return;
    setStatut.mutate(
      {
        id: lab.id,
        statut: TARGET_STATUT[action],
        ...(motif.trim() ? { motif: motif.trim() } : {}),
        expected_version: lab.version,
      },
      {
        onSuccess: () => {
          toast.success(t(`mgmt.onboarding.lifecycle.${action}.done`));
          setMotif("");
          onClose();
        },
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : t("mgmt.onboarding.error.generic")),
      },
    );
  };

  const name = lab ? (lab.raisonSociale ?? lab.nomCommercial ?? lab.reference ?? "") : "";

  return (
    <Modal
      open={open}
      onClose={close}
      dismissable={!setStatut.isPending}
      title={action ? t(`mgmt.onboarding.lifecycle.${action}.title`) : ""}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={setStatut.isPending}>
            {t("mgmt.onboarding.action.cancel")}
          </Button>
          <Button
            variant={action === "suspend" ? "danger" : "primary"}
            onClick={confirm}
            loading={setStatut.isPending}
          >
            {action ? t(`mgmt.onboarding.lifecycle.${action}.confirm`) : t("mgmt.onboarding.action.confirm")}
          </Button>
        </>
      }
    >
      {lab && action && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
          <p style={{ margin: 0 }}>{t(`mgmt.onboarding.lifecycle.${action}.body`, { name })}</p>
          <FormField label={t("mgmt.onboarding.lifecycle.motif")}>
            {(p) => (
              <Textarea
                {...p}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={3}
                placeholder={t("mgmt.onboarding.lifecycle.motif.placeholder")}
              />
            )}
          </FormField>
        </div>
      )}
    </Modal>
  );
}
